import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Paper,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import TextSearch from '../../controls/TextSearch';
import { useNavigate } from 'react-router-dom';
import { useCanReadParentOrgs } from '../../../hooks/permissions/useCanReadParentOrgs';
import { useCanReadAdministration } from '../../../hooks/permissions/useCanReadAdministration';
import { useCanReadTransportationAttributes } from '../../../hooks/permissions/useCanReadTransportationAttributes';
import { useCanReadPricingModels } from '../../../hooks/permissions/useCanReadPricingModels';
import { useCanReadTransportationBenefits } from '../../../hooks/permissions/useCanReadTransportationBenefits';
import { useCanReadWorkFlows } from '../../../hooks/permissions/useCanReadWorkFlows';
import { useIsClientPortalAdminUser } from '../../../hooks/useIsClientPortalAdminUser';
import { useCanReadAiServices } from '../../../hooks/permissions/useCanReadAiServices';
import { useCanReadPurposeCodes } from '../../../hooks/permissions/useCanReadPurposeCodes';
import { useCanReadServiceLevelModels } from '../../../hooks/permissions/useCanReadServiceLevelModels';

const PREFIX = 'Settings';

const classes = {
  container: `${PREFIX}-container`,
  content: `${PREFIX}-content`,
  header: `${PREFIX}-header`,
  search: `${PREFIX}-search`,
  settings: `${PREFIX}-settings`,
  title: `${PREFIX}-title`,
  group: `${PREFIX}-group`,
  subtitle: `${PREFIX}-subtitle`,
  cards: `${PREFIX}-cards`,
  card: `${PREFIX}-card`,
  cardTitle: `${PREFIX}-cardTitle`,
  cardDescription: `${PREFIX}-cardDescription`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: theme.spacing(3),
  },
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(4),
    justifyContent: 'space-between',
    gap: theme.spacing(5),
  },
  [`& .${classes.search}`]: {
    width: 266,
  },
  [`& .${classes.header}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: theme.spacing(4),
  },
  [`& .${classes.settings}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: theme.spacing(7),
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontStyle: 'normal',
    fontWeight: 700,
    fontSize: 30,
    lineHeight: theme.spacing(5),
    letterSpacing: '0.25px',
    color: 'rgba(1, 16, 18, 0.9)',
  },
  [`& .${classes.group}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
  },
  [`& .${classes.subtitle}`]: {
    fontFamily: 'Bitter',
    fontStyle: 'normal',
    fontWeight: 700,
    fontSize: 21,
    lineHeight: theme.spacing(4),
    color: 'rgba(1, 16, 18, 0.9)',
  },
  [`& .${classes.cards}`]: {
    display: 'flex',
    gap: theme.spacing(3),
    flexWrap: 'wrap',
  },
  [`& .${classes.card}`]: {
    display: 'flex',
    width: 246,
    height: 28,
    padding: theme.spacing(1, 2),
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  [`& .${classes.cardTitle}`]: {
    fontFamily: 'Source Sans Pro',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: 16,
    lineHeight: '175%',
    letterSpacing: '0.15px',
    color: 'rgba(1, 16, 18, 0.9)',
  },
  [`& .${classes.cardDescription}`]: {
    fontFamily: 'Source Sans Pro',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: '143%',
    letterSpacing: '0.17px',
    color: 'rgba(1, 16, 18, 0.6)',
  },
}));

interface SettingGroup {
  title: string;
  cards: {
    name: string;
    description: string;
    link: string;
    visible: boolean;
  }[];
}

const SettingGroup = ({ group }: { group: SettingGroup }) => {
  const navigate = useNavigate();

  const onCardClick = (url: string) => {
    navigate(url);
  };

  return (
    <Box className={classes.group}>
      <Typography variant="h3" className={classes.subtitle}>
        {group.title}
      </Typography>
      <Box className={classes.cards}>
        {group.cards.map(
          (card) =>
            card.visible && (
              <Card key={`${card.link}-${card.name}`}>
                <CardActionArea onClick={() => onCardClick(card.link)}>
                  <CardContent className={classes.card}>
                    <Box className={classes.cardTitle}>{card.name}</Box>
                    <Box className={classes.cardDescription}>
                      {card.description}
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            ),
        )}
      </Box>
    </Box>
  );
};

export default function Settings() {
  const { t } = useTranslation('common');

  const canViewParentOrgPage = useCanReadParentOrgs();
  const canViewPricingModelsPage = useCanReadPricingModels();
  const canViewTransportationBenefitsPage = useCanReadTransportationBenefits();
  const canViewTransportationAttributesPage =
    useCanReadTransportationAttributes();
  const canViewServiceLevelModelsPage = useCanReadServiceLevelModels();
  const canViewAdministrationPage = useCanReadAdministration();
  const canViewNotificationsPage = useCanReadWorkFlows();
  const isClientPortalAdminUser = useIsClientPortalAdminUser();
  const canViewAiServicesPage = useCanReadAiServices();
  const canViewPurposeCodes = useCanReadPurposeCodes();

  const [currentSearchText, setCurrentSearchText] = useState('');

  // TODO: Need to implement the following: Purpose Code Models, Work Queues, Work Flows, Invoices, Coverage Areas, Labels
  const settingGroups: SettingGroup[] = isClientPortalAdminUser
    ? [
        {
          title: t('settings.company'),
          cards: [
            {
              name: t('settings.users'),
              description: '',
              link: '/users',
              visible: isClientPortalAdminUser,
            },
          ],
        },
      ]
    : [
        {
          title: t('settings.americanLogisticsModels'),
          cards: [
            {
              name: t('settings.serviceLevelModels'),
              description: '',
              link: '/service-level-models',
              visible: canViewServiceLevelModelsPage,
            },
            {
              name: t('settings.transportationBenefitModels'),
              description: '',
              link: '/transportation-benefits',
              visible: canViewTransportationBenefitsPage,
            },
            {
              name: t('navigation.transportationAttributes'),
              description: '',
              link: '/transportation-attributes',
              visible: canViewTransportationAttributesPage,
            },
            {
              name: t('settings.pricingModels'),
              description: '',
              link: '/pricingmodels',
              visible: canViewPricingModelsPage,
            },
            {
              name: t('settings.purposeCodeModels'),
              description: '',
              link: '/purposecodes',
              visible: canViewPurposeCodes,
            },
            {
              name: t('settings.admin'),
              description: '',
              link: '/admin',
              visible: canViewAdministrationPage,
            },
          ],
        },
        {
          title: t('settings.aiServicesGroup'),
          cards: [
            {
              name: t('settings.aiChat'),
              description: '',
              link: '/ai-services',
              visible: canViewAiServicesPage,
            },
          ],
        },
        {
          title: t('settings.automationGroup'),
          cards: [
            {
              name: t('settings.workQueues'),
              description: '',
              link: '/settings',
              visible: canViewAdministrationPage,
            },
            {
              name: t('settings.workflows'),
              description: '',
              link: '/settings',
              visible: canViewAdministrationPage,
            },
            {
              name: t('settings.notifications'),
              description: '',
              link: '/simple-notifications',
              visible: canViewNotificationsPage,
            },
            {
              name: t('settings.inventoryManagement'),
              description: '',
              link: '/inventory-management',
              visible: canViewAdministrationPage,
            },
          ],
        },
        {
          title: t('settings.clientsGroup'),
          cards: [
            {
              name: t('navigation.parentOrganizations'),
              description: '',
              link: '/parent-organizations',
              visible: canViewParentOrgPage,
            },
          ],
        },
        {
          title: t('settings.providerRelations'),
          cards: [
            {
              name: t('settings.invoices'),
              description: '',
              link: '/settings',
              visible: canViewAdministrationPage,
            },
            {
              name: t('settings.coverageAreas'),
              description: '',
              link: '/settings',
              visible: canViewAdministrationPage,
            },
            {
              name: t('settings.tripManagement'),
              description: '',
              link: '/settings',
              visible: canViewAdministrationPage,
            },
            {
              name: t('settings.rideshare'),
              description: '',
              link: '/rideshare',
              visible: canViewAdministrationPage,
            },
          ],
        },
        {
          title: t('settings.userAdmin'),
          cards: [
            {
              name: t('navigation.permissions'),
              description: '',
              link: '/permissions',
              visible: canViewAdministrationPage,
            },
          ],
        },
        {
          title: t('settings.customizationGroup'),
          cards: [
            {
              name: t('settings.lookups'),
              description: '',
              link: '/lookups',
              visible: canViewAdministrationPage,
            },
            {
              name: t('settings.applicationGlobalSettings'),
              description: '',
              link: '/application-global-settings',
              visible: canViewAdministrationPage,
            },
          ],
        },
      ];

  const [filteredGroups, setFilteredGroups] = useState<SettingGroup[]>([]);

  const filterSettingGroups = (text: string) => {
    const lcText = text.toLowerCase();
    const filtered = settingGroups.map((group) => {
      const cards = group.cards.filter((card) => {
        const lcName = card.name.toLowerCase();
        const lcDescription = card.description.toLowerCase();
        return (
          (card.visible && lcName.indexOf(lcText) >= 0) ||
          lcDescription.indexOf(lcText) >= 0
        );
      });
      const newGroup = { ...group, cards };
      return newGroup;
    });
    setFilteredGroups(filtered.filter((f) => f.cards.length > 0));
  };

  useEffect(() => {
    filterSettingGroups('');
  }, []);

  const onSearchTextChange = (val: string) => {
    setCurrentSearchText(val);
    filterSettingGroups(val);
  };

  return (
    <StyledBox className={classes.container}>
      <Paper className={classes.content}>
        <Box className={classes.header}>
          <Typography variant="h2" className={classes.title}>
            {t('settings.title')}
          </Typography>
          {!isClientPortalAdminUser && (
            <TextSearch
              id="text-search"
              icon={<SearchIcon />}
              className={classes.search}
              label={t('common.search')}
              placeholder={t('common.search')}
              currentSearchFilter={currentSearchText}
              onSearchChange={onSearchTextChange}
            />
          )}
        </Box>
        <Box className={classes.settings}>
          {filteredGroups.map((group) => {
            const hasVisibleCard = group.cards.some((card) => card.visible);
            return hasVisibleCard ? (
              <SettingGroup key={group.title} group={group} />
            ) : null;
          })}
        </Box>
      </Paper>
    </StyledBox>
  );
}
